import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { Link, useParams } from 'react-router-dom'


import { api, ApiError } from '@/lib/api'

type PlanSummary = {
  id: string
  name: string
  stripePriceId: string
  interval: string
  creditsPerMonth: number
  rolloverCap: number
  features: Record<string, boolean>
}

type TenantDetailResponse = {
  tenant: { id: string; slug: string; name: string; featureFlags: Record<string, boolean>; createdAt: string }
  credits: { balance: number; totalUsed: number; totalPurchased: number } | null
  counts: { memberCount: number; endClientCount: number; websiteCount: number }
  subscription: { status: string; currentPeriodEnd: string; planId: string | null } | null
  plan: PlanSummary | null
  customPlan: PlanSummary | null
}

export function AdminTenantDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data, isLoading } = useQuery({
    queryKey: ['lensora-admin', 'tenants', id],
    queryFn: () => api.get<TenantDetailResponse>(`/api/lensora-admin/tenants/${id}`),
    enabled: !!id,
  })

  if (isLoading || !data) {
    return <div className="flex h-40 items-center justify-center"><Spinner /></div>
  }

  const { tenant, credits, counts, subscription, plan, customPlan } = data

  const activeFlags = Object.entries(tenant.featureFlags)
    .filter(([, v]) => v)
    .map(([k]) => k.replace(/_/g, ' '))

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/tenants">← Tenants</Link>
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-text">{tenant.name}</h1>
          <Badge variant="outline" className="font-mono">{tenant.slug}</Badge>
        </div>
        <p className="mt-1 text-sm text-text-muted">
          Created {new Date(tenant.createdAt).toLocaleDateString()}
          {activeFlags.length > 0 && ` · ${activeFlags.join(', ')}`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard title="Members" value={String(counts.memberCount)} />
        <StatCard title="End clients" value={String(counts.endClientCount)} />
        <StatCard title="Websites" value={String(counts.websiteCount)} />
        <StatCard title="Credit balance" value={String(credits?.balance ?? 0)} />
        <StatCard title="Credits used" value={String(credits?.totalUsed ?? 0)} />
      </div>

      <BillingCard
        tenantId={tenant.id}
        subscription={subscription}
        plan={plan}
        customPlan={customPlan}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team members</CardTitle>
          <CardDescription>
            <Button asChild variant="link" className="h-auto p-0 text-xs">
              <Link to={`/admin/users?tenantId=${tenant.id}`}>Filter users by this tenant →</Link>
            </Button>
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}

// White-label billing: create the tenant's custom plan (Stripe price +
// billing_plans row), then generate a checkout link to send to the owner.
function BillingCard({
  tenantId,
  subscription,
  plan,
  customPlan,
}: {
  tenantId: string
  subscription: TenantDetailResponse['subscription']
  plan: PlanSummary | null
  customPlan: PlanSummary | null
}) {
  const queryClient = useQueryClient()
  const [planName, setPlanName] = React.useState('White Label')
  const [monthlyAmount, setMonthlyAmount] = React.useState('')
  const [creditsPerMonth, setCreditsPerMonth] = React.useState('')
  const [rolloverCap, setRolloverCap] = React.useState('')
  const [checkoutUrl, setCheckoutUrl] = React.useState<string | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const createPlanMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/lensora-admin/tenants/${tenantId}/billing-plan`, {
        name: planName,
        monthlyAmountGbp: Number(monthlyAmount),
        creditsPerMonth: Number(creditsPerMonth || 0),
        rolloverCap: Number(rolloverCap || 0),
      }),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['lensora-admin', 'tenants', tenantId] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create plan'),
  })

  const checkoutLinkMutation = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>(`/api/lensora-admin/tenants/${tenantId}/checkout-link`, {
        successUrl: `${window.location.origin}/dashboard/settings/billing?checkout=success`,
        cancelUrl: `${window.location.origin}/dashboard/settings/billing?checkout=cancelled`,
      }),
    onSuccess: ({ url }) => {
      setError(null)
      setCheckoutUrl(url)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to create checkout link'),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Billing</CardTitle>
        <CardDescription>
          Custom white-label subscription for this tenant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {subscription ? (
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium text-text">{plan?.name ?? 'Subscription'}</p>
              <p className="text-sm text-text-muted">
                Renews {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                {plan ? ` · ${plan.creditsPerMonth.toLocaleString()} credits/mo` : ''}
              </p>
            </div>
            <Badge
              variant={
                subscription.status === 'active' || subscription.status === 'trialing'
                  ? 'success'
                  : 'warning'
              }
              className="capitalize"
            >
              {subscription.status.replace('_', ' ')}
            </Badge>
          </div>
        ) : customPlan ? (
          <div className="space-y-3">
            <p className="text-sm text-text-muted">
              Custom plan <span className="font-medium text-text">{customPlan.name}</span>{' '}
              ({customPlan.creditsPerMonth.toLocaleString()} credits/mo) is ready — no
              subscription yet. Generate a checkout link and send it to the tenant owner.
            </p>
            {checkoutUrl ? (
              <div className="flex items-center gap-2">
                <Input readOnly value={checkoutUrl} className="font-mono text-xs" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(checkoutUrl)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => checkoutLinkMutation.mutate()}
                disabled={checkoutLinkMutation.isPending}
              >
                {checkoutLinkMutation.isPending ? 'Creating…' : 'Generate checkout link'}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-muted">
              No plan yet. Create the agency's custom plan — this creates a Stripe price on
              the White Label product and binds it to this tenant.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="plan-name">Plan name</Label>
                <Input id="plan-name" value={planName} onChange={(e) => setPlanName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plan-amount">Monthly price (GBP)</Label>
                <Input
                  id="plan-amount"
                  type="number"
                  min="1"
                  placeholder="750"
                  value={monthlyAmount}
                  onChange={(e) => setMonthlyAmount(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plan-credits">Credits per month</Label>
                <Input
                  id="plan-credits"
                  type="number"
                  min="0"
                  placeholder="10000"
                  value={creditsPerMonth}
                  onChange={(e) => setCreditsPerMonth(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plan-rollover">Rollover cap</Label>
                <Input
                  id="plan-rollover"
                  type="number"
                  min="0"
                  placeholder="2000"
                  value={rolloverCap}
                  onChange={(e) => setRolloverCap(e.target.value)}
                />
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => createPlanMutation.mutate()}
              disabled={createPlanMutation.isPending || !monthlyAmount || !planName}
            >
              {createPlanMutation.isPending ? 'Creating…' : 'Create custom plan'}
            </Button>
          </div>
        )}
        {error && <p className="text-sm text-feedback-error">{error}</p>}
      </CardContent>
      {subscription && (
        <CardFooter>
          <p className="text-xs text-text-muted">
            Plan changes and cancellation are managed through Stripe; feature flags and
            credits follow the subscription automatically.
          </p>
        </CardFooter>
      )}
    </Card>
  )
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardDescription className="text-xs">{title}</CardDescription>
        <p className="text-2xl font-bold text-text">{value}</p>
      </CardHeader>
    </Card>
  )
}
